import React, { FocusEvent, useCallback, useContext, useEffect, useRef } from 'react';
import { useFormik } from 'formik';

import type { PasswordFormValues } from '../../../types';

import { URL } from 'app/api/constants';
import { formErrorsDisplayDelay } from 'app/constants/formConstants';
import { FormControl } from 'app/design-system/components/FormControl';
import PasswordInput from 'app/design-system/components/Input/PasswordInput';
import { useTranslation } from 'app/lang';

import { sendRequest } from '../../../actions';
import { CHECK_TIME_DELAY, PASSWORD_FORM_VALUES } from '../../../constants';
import { StepContext } from '../../../contexts/stepContext';
import { UserContext } from '../../../contexts/userContext';
import { getValidate } from './password-form-validations';

import dictionary from './PasswordForm.dictionary.json';

import styles from '../../../RestorePasswordDialog.module.scss';

const PasswordForm = (): JSX.Element => {
  const { t } = useTranslation();
  const { userData: { token, requisite }, setUserData } = useContext(UserContext);
  const { activeStep, forwardStep } = useContext(StepContext);
  const blurTimeout = useRef<number>();
  const validate = getValidate(t);
  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setTouched,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: PASSWORD_FORM_VALUES,
    validate,
    onSubmit: (values: PasswordFormValues, formikHelpers) => {
      const { newPass: password } = values;

      setUserData((prevState) => ({ ...prevState, disabled: true }));

      sendRequest(URL.PASSWORD_RESET_SEND_NEW_PASS, { password, token })
        .then(() => {
          setUserData((prevState) => ({ ...prevState, checked: true }));

          setTimeout(() => {
            setUserData((prevState) => ({ ...prevState, checked: false }));
            forwardStep(activeStep);
          }, CHECK_TIME_DELAY);
        })
        .catch(() => {
          formikHelpers.setSubmitting(false);
          formikHelpers.setFieldError('newPass', 'Введи пароль от 8 символов');
        })
        .finally(() => setUserData((prevState) => ({ ...prevState, disabled: false })));
    },
  });

  useEffect(() => {
    setFieldValue('login', requisite);
  }, []);

  const newPasswordError = touched.newPass && errors.newPass;
  const confirmPasswordError = touched.confirmPass && errors.confirmPass;

  const showCaption = (
    (values.newPass && values.confirmPass) && (values.newPass === values.confirmPass)
  );

  const handleBlur = useCallback((event: FocusEvent): void => {
    if (blurTimeout) {
      clearTimeout(blurTimeout.current);
    }
    if (
      event.target instanceof HTMLButtonElement
        && event.target.name === 'password-icon'
    ) {
      setTouched({ ['newPass']: true, ['confirmPass']: true });
    }
    blurTimeout.current = window.setTimeout(() => (
      setTouched({ ['newPass']: true, ['confirmPass']: true })
    ), formErrorsDisplayDelay);
  },
  [blurTimeout],
  );

  const handleFocus = useCallback(
    (fieldName: keyof typeof PASSWORD_FORM_VALUES) => (): void => {
      if (blurTimeout) {
        clearTimeout(blurTimeout.current);
        blurTimeout.current = undefined;
      }
      setFieldTouched(fieldName, false);
    },
    [blurTimeout],
  );

  useEffect(() => (): void => {
    if (blurTimeout) {
      clearTimeout(blurTimeout.current);
    }
  }, []);

  return (
    <form id={activeStep.toString()}
          onSubmit={handleSubmit}>
      <div className={styles.newPassInputs}>
        <FormControl label={t(dictionary.newPassLabel)}
                     className={styles.passwordFormControl}
                     error={newPasswordError}
                     data-test="Modal-restorePassword-newPassLabel">
          <PasswordInput name="newPass"
                         value={values.newPass}
                         error={!!newPasswordError}
                         placeholder={t(dictionary.newPassPlaceholder)}
                         onFocus={handleFocus('newPass')}
                         onIconFocus={handleFocus('newPass')}
                         onBlur={handleBlur}
                         onIconBlur={handleBlur}
                         onChange={handleChange}
                         disabled={isSubmitting}
                         className={styles.passwordInput}
                         data-test="Modal-restorePassword-newPassInput" />
        </FormControl>
        <FormControl label={t(dictionary.confirmPassLabel)}
                     className={styles.passwordFormControl}
                     error={!showCaption && confirmPasswordError}
                     caption={showCaption && t(dictionary.confirmPassCaption)}
                     data-test="Modal-restorePassword-confirmPassLabel">
          <PasswordInput name="confirmPass"
                         value={values.confirmPass}
                         error={!showCaption && !!confirmPasswordError}
                         placeholder={t(dictionary.confirmPassPlaceholder)}
                         onFocus={handleFocus('confirmPass')}
                         onIconFocus={handleFocus('confirmPass')}
                         onBlur={handleBlur}
                         onIconBlur={handleBlur}
                         onChange={handleChange}
                         disabled={isSubmitting}
                         className={styles.passwordInput}
                         data-test="Modal-restorePassword-confirmPassInput" />
        </FormControl>
      </div>
    </form>
  );
};

export default PasswordForm;
